import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type { ComponentProps } from "react";

import { cn } from "helpers/cn";

import { Spinner } from "./Spinner";

const buttonVariants = cva(
  "btn-colors relative inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-solid px-[15px] text-body-small font-normal whitespace-nowrap transition-all ease-[cubic-bezier(0.645,0.045,0.355,1)] select-none motion-reduce:transition-none focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-[#d4e6c3] disabled:cursor-not-allowed disabled:border-[#d9d9d9] disabled:text-(--seedtime-control-disabled-text) disabled:shadow-none disabled:*:pointer-events-none aria-disabled:pointer-events-none aria-disabled:border-[#d9d9d9] aria-disabled:text-(--seedtime-control-disabled-text) aria-disabled:shadow-none aria-busy:cursor-default aria-busy:opacity-65 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        solid:
          "border-transparent bg-(--btn-solid) text-white shadow-(--btn-shadow) hover:bg-(--btn-solid-hover) active:bg-(--btn-solid-active) disabled:bg-background aria-disabled:bg-background",
        outlined:
          "border-(--btn-border) bg-card text-(--btn) shadow-(--btn-shadow) hover:border-(--btn-hover) hover:text-(--btn-hover) active:border-(--btn-active) active:text-(--btn-active) disabled:bg-background aria-disabled:bg-background",
        text: "border-transparent bg-transparent text-(--btn) hover:bg-(--btn-text-hover-bg) hover:text-(--btn-text-hover) active:bg-(--btn-text-active-bg) active:text-(--btn-text-active) disabled:border-transparent disabled:bg-transparent aria-disabled:border-transparent aria-disabled:bg-transparent",
        link: "border-transparent bg-transparent text-(--btn) hover:text-(--btn-link-hover) active:text-(--btn-active) disabled:border-transparent disabled:bg-transparent aria-disabled:border-transparent aria-disabled:bg-transparent"
      },
      size: {
        small: "h-6 rounded-md px-3",
        default: "h-8",
        large: "h-10 rounded-[10px]",
        touch: "h-11"
      },
      shape: {
        default: "",
        square: "aspect-square px-0",
        circle: "aspect-square rounded-full px-0"
      },
      block: {
        true: "w-full"
      },
      ghost: {
        true: "bg-transparent shadow-none hover:bg-transparent active:bg-transparent"
      }
    },
    defaultVariants: {
      variant: "outlined",
      size: "default",
      shape: "default"
    }
  }
);

type ButtonColor = "primary" | "neutral" | "danger";

type ButtonProps = Omit<ComponentProps<"button">, "color"> &
  Omit<VariantProps<typeof buttonVariants>, "ghost"> & {
    /** Renders the child element as the button. `disabled` becomes `aria-disabled`, because the child may not support `disabled`. */
    asChild?: boolean;
    /** Defaults to `primary` for `solid` and `link`, and to `neutral` for the other variants. */
    color?: ButtonColor;
    /** Transparent background, for use on a coloured surface. Has no effect on variants other than `outlined`. */
    ghost?: boolean;
    /** Shows a spinner, sets `aria-busy` and ignores clicks. The button stays focusable. */
    loading?: boolean;
  };

const Button = ({
  color,
  variant = "outlined",
  size,
  shape,
  block,
  asChild = false,
  ghost = false,
  loading = false,
  disabled = false,
  type = "button",
  className,
  children,
  onClick,
  ...props
}: ButtonProps) => {
  const Component = asChild ? Slot.Root : "button";
  const resolvedColor =
    color ?? (variant === "solid" || variant === "link" ? "primary" : "neutral");

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return (
    <Component
      aria-busy={loading || undefined}
      aria-disabled={asChild && disabled ? true : undefined}
      className={cn(
        buttonVariants({
          variant,
          size,
          shape,
          block,
          ghost: ghost && variant === "outlined"
        }),
        className
      )}
      data-color={resolvedColor}
      data-slot="button"
      disabled={asChild ? undefined : disabled}
      type={type}
      onClick={handleClick}
      {...props}
    >
      {loading && <Spinner aria-hidden className="text-current" size="small" />}
      <Slot.Slottable>{children}</Slot.Slottable>
    </Component>
  );
};

export { Button, buttonVariants };
export type { ButtonColor, ButtonProps };
